import React, { FC } from 'react';
import { UserNameWithEmail } from '../name_component/user/UserNameWithEmail';
import { nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { AOPEventDescription } from './utils/AOPExecutionUtils';

/**
 * Whoever asked for something, by the address the service names them with.
 *
 * Radar identifies the person who paused, resumed or cancelled a run by their
 * email and nothing else, so every Radar surface was printing
 * `arnav+sadmin@useopenwrench.com` where the rest of the app shows a name.
 *
 * The address is the fallback, and it is needed twice over.
 * `UserNameWithEmail` renders nothing when it cannot resolve a contact — right
 * in a column, wrong mid-sentence. And a contact that resolves carrying no name
 * is the same blank by another route: the name is composed by substituting
 * `nameGiven nameFamily`, so a record with neither comes out a space. That is
 * the one that showed, on an account whose contact exists without a name.
 */
export const AOPActorName: FC<{ email: string }> = ({ email }) => (
	<UserNameWithEmail
		mode="inline"
		email={email}
		// No contact at all.
		fallback={email}
		// A contact with no name on it. `renderName` stands in for the whole
		// element, which is what makes the empty case reachable from out here.
		renderName={(contact: any) => {
			const name = `${nullSafeGetOrElse('nameGiven', contact, '')} ${nullSafeGetOrElse(
				'nameFamily',
				contact,
				''
			)}`.trim();

			return name ? name : email;
		}}
	/>
);

/**
 * An event's sentence, with the person rendered where it names one.
 *
 * `describeEvent` keeps the address out of the sentence — a name is a
 * component, not a word — and this puts the two back together, so the screens
 * reading a description cannot word it three ways.
 *
 * The full stop belongs here rather than to `AOPActorName`: a session header
 * carries the same name and then goes on with "at 2:31 PM", so the name and
 * the punctuation after it are not the same decision.
 */
export const AOPEventSentence: FC<{ described: AOPEventDescription }> = ({ described }) => {
	if (!described.actor) {
		return <>{described.title}</>;
	}

	return (
		<>
			{described.title} <AOPActorName email={described.actor} />.
		</>
	);
};

export default AOPEventSentence;
